import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '@services/api';

interface Issue {
  id: number;
  issue_number: string;
  title: string;
  status: string;
}

interface Props {
  projectId: number;
  selectedIssues: string[];
  onChange: (issues: string[]) => void;
  currentIssueId?: number;
}

export function IssueLinkSelector({ projectId, selectedIssues, onChange, currentIssueId }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Issue[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Search issues
  useEffect(() => {
    const searchIssues = async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await api.get<any>(
          `/api/issues?project_id=${projectId}&search=${encodeURIComponent(searchTerm)}&limit=10`
        );

        if (response.success && response.data && response.data.items) {
          // Filter out current issue and already selected issues
          const filtered = response.data.items.filter(
            (issue: Issue) =>
              issue.id !== currentIssueId &&
              !selectedIssues.includes(issue.issue_number)
          );
          setSearchResults(filtered);
          setShowDropdown(filtered.length > 0);
        }
      } catch (error) {
        console.error('Failed to search issues:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchIssues, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, projectId, currentIssueId, selectedIssues]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectIssue = (issueNumber: string) => {
    onChange([...selectedIssues, issueNumber]);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleRemoveIssue = (issueNumber: string) => {
    onChange(selectedIssues.filter(n => n !== issueNumber));
  };

  const handleManualAdd = () => {
    const trimmed = searchTerm.trim();
    if (trimmed && !selectedIssues.includes(trimmed)) {
      onChange([...selectedIssues, trimmed]);
      setSearchTerm('');
      setShowDropdown(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchResults.length > 0) {
        handleSelectIssue(searchResults[0].issue_number);
      } else {
        handleManualAdd();
      }
    }
  };

  return (
    <div class="space-y-2">
      <label class="block text-sm font-medium">연결된 이슈</label>

      {/* Selected Issues */}
      {selectedIssues.length > 0 && (
        <div class="flex flex-wrap gap-2 mb-2">
          {selectedIssues.map((issueNumber) => (
            <span
              key={issueNumber}
              class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
            >
              {issueNumber}
              <button
                type="button"
                onClick={() => handleRemoveIssue(issueNumber)}
                class="hover:text-blue-600"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div class="relative" ref={dropdownRef}>
        <input
          type="text"
          value={searchTerm}
          onInput={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
          class="input w-full"
          placeholder="이슈 번호 또는 제목으로 검색..."
        />

        {isSearching && (
          <div class="absolute right-3 top-2.5 text-gray-400">
            검색 중...
          </div>
        )}

        {/* Dropdown */}
        {showDropdown && searchResults.length > 0 && (
          <div class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {searchResults.map((issue) => (
              <button
                key={issue.id}
                type="button"
                onClick={() => handleSelectIssue(issue.issue_number)}
                class="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-200 last:border-0"
              >
                <div class="font-medium text-blue-600 text-sm font-mono">
                  {issue.issue_number}
                </div>
                <div class="text-sm text-gray-700 line-clamp-1">
                  {issue.title}
                </div>
                <div class="text-xs text-gray-500 mt-1">
                  상태: {issue.status}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <p class="text-xs text-gray-500">
        이슈를 검색하여 선택하거나, 직접 이슈 번호를 입력하세요
      </p>
    </div>
  );
}
